import IconButton from 'components/@extended/IconButton';
import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { CreateDialogSection } from 'components/create-dialog/CreateDialogSection';
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from 'components/icons';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { Separator } from 'components/ui/separator';
import React, { useId, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';
import { getIdLink } from 'utils/linkUtil';
import { SectionTitleProvider, useHighlightedFieldsCounts, useSectionActions } from './SectionActionsContext';
import { SubSectionProps } from './SubSection';

/**
 * A section's (and a sub-section's) column of fields. The 16px gap is the only space between them: a
 * field's own margin is zeroed, `!` so that a margin utility the field carries under a variant of its
 * own (a breakpoint, `last:`) is zeroed too.
 */
export const SECTION_FIELDS_CLASS = 'flex flex-col gap-4 *:m-0!';

/**
 * The kit's ghost button paints an open popup's trigger on `aria-expanded`. A section is expanded at
 * rest, so that state keeps the resting and hover look instead.
 */
const CHEVRON_CLASS = cn(
    'text-fg-secondary hover:bg-muted hover:text-fg size-[30px]',
    'aria-expanded:text-fg-secondary aria-expanded:bg-transparent',
    'aria-expanded:hover:bg-muted aria-expanded:hover:text-fg'
);

export type SectionProps = {
    title: string;
    children:
        | (React.ReactElement<SubSectionProps> | React.ReactNode)[]
        | (React.ReactElement<SubSectionProps> | React.ReactNode);

    isHidden?: boolean;
    readonly?: boolean;
    noTranslation?: boolean;
    tooltip?: JSX.Element;
    collapsible?: boolean;
    defaultCollapsed?: boolean;
};

/** A form section: a card on a page, an accordion in the create dialog. */
function Section(props: SectionProps) {
    const inCreateDialog = useInCreateDialog();

    return inCreateDialog ? <CreateDialogSection {...props} /> : <PageSection {...props} />;
}

function PageSection({
    title,
    children,
    isHidden = false,
    noTranslation = false,
    tooltip,
    collapsible = false,
    defaultCollapsed = false,
}: SectionProps) {
    // TODO(react-doctor: no-derived-useState): `defaultCollapsed` seeds local state but
    // users toggle `collapsed` independently; this is the legitimate "initial value from
    // prop" pattern, not prop-mirroring. Replacing with derived state would lose user
    // toggles. Intentionally left as-is.
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const titleId = useId();
    const { collapseSignal, expandSignal, expandSectionSignal, expandSectionTitle } = useSectionActions();
    const sectionHighlightCounts = useHighlightedFieldsCounts();
    const highlightCount = sectionHighlightCounts[title] ?? 0;

    // prevSignals values are only compared during render, never read in JSX,
    // so a ref is sufficient (avoids a redundant render trigger from setState).
    const prevSignalsRef = useRef({ collapse: 0, expand: 0, expandSection: 0 });

    if (collapsible && collapseSignal !== prevSignalsRef.current.collapse) {
        prevSignalsRef.current = { ...prevSignalsRef.current, collapse: collapseSignal };
        if (collapseSignal > 0) setCollapsed(true);
    }

    if (collapsible && expandSignal !== prevSignalsRef.current.expand) {
        prevSignalsRef.current = { ...prevSignalsRef.current, expand: expandSignal };
        if (expandSignal > 0) setCollapsed(false);
    }

    if (expandSectionSignal !== prevSignalsRef.current.expandSection) {
        prevSignalsRef.current = { ...prevSignalsRef.current, expandSection: expandSectionSignal };
        if (expandSectionSignal > 0 && expandSectionTitle === title) {
            setCollapsed(false);
        }
    }

    if (isHidden) return null;

    const sectionId = getIdLink(noTranslation ? title.toLocaleLowerCase() : title);

    return (
        <Collapsible
            open={!collapsed}
            onOpenChange={(open) => setCollapsed(!open)}
            className="bg-paper border-divider shadow-section new:border-0 new:shadow-card new:outline-hidden w-full rounded-md border"
        >
            <div
                id={sectionId}
                data-section-title={title}
                {...(noTranslation ? { 'data-no-translation': noTranslation } : {})}
                className={cn(
                    'flex scroll-mt-24 items-center justify-between px-6 py-4 text-base leading-6 font-bold',
                    collapsible ? 'cursor-pointer' : 'cursor-default'
                )}
                onClick={collapsible ? () => setCollapsed((prev) => !prev) : undefined}
            >
                <span className="flex items-center gap-1">
                    <span id={titleId}>{noTranslation ? title : <FormattedMessage id={title} />}</span>
                    {tooltip ? tooltip : null}
                </span>
                <div className="ml-auto flex items-center">
                    {highlightCount > 0 && (
                        <div
                            className={cn(
                                'bg-warning/15 text-warning-dark new:text-caption flex h-5 min-w-5 shrink-0 items-center justify-center rounded-[10px] px-1 text-[11px] font-bold',
                                collapsible && 'mr-2'
                            )}
                        >
                            {highlightCount}
                        </div>
                    )}
                    {collapsible && (
                        <CollapsibleTrigger
                            // The whole header row toggles too; stopping here keeps one click one toggle.
                            onClick={(e) => e.stopPropagation()}
                            render={
                                // Named by the section's title; the trigger's `aria-expanded` says whether it is open.
                                <IconButton size="small" aria-labelledby={titleId} className={CHEVRON_CLASS}>
                                    <ExpandMoreRoundedIcon
                                        className={cn(
                                            'new:motion-safe:transition-transform new:duration-(--duration-fast) new:ease-(--ease-smooth-out)',
                                            !collapsed && '-scale-y-100'
                                        )}
                                    />
                                </IconButton>
                            }
                        />
                    )}
                </div>
            </div>
            {/* Kept mounted while collapsed, so the fields inside still register and validate. */}
            <CollapsibleContent keepMounted>
                <Separator />
                <SectionTitleProvider value={title}>
                    <div className={cn(SECTION_FIELDS_CLASS, 'px-6 py-3')}>{children}</div>
                </SectionTitleProvider>
            </CollapsibleContent>
        </Collapsible>
    );
}

Section.displayName = 'Section';

export default Section;
